import { useMutation } from '@tanstack/react-query';

import { tokenRequests, UploadDataDetails } from '../api/api';
import { Codeowners } from '../consts/codeowners';
import { useOnlineBookingReducer } from '../state/useOnlineBookingReducer';
import { captureException, statusCodesMapper } from '../utils/exception';
import { getMimeType } from '../utils/getMimeType';

type Props = {
  cartItemUUID: string;
  token?: string;
};

export const useUploadPhoto = ({ cartItemUUID, token }: Props) => {
  const { dispatch, actions } = useOnlineBookingReducer();
  const { mutateAsync } = useMutation(tokenRequests.getUploadData);

  const uploadPhoto = async (photo: File) => {
    const mimeType = await getMimeType(photo);

    if (!token || !mimeType) {
      dispatch(actions.setLoadingForPhoto(cartItemUUID, photo, false));
      dispatch(actions.setErrorForPhoto(cartItemUUID, photo));
      return;
    }

    const response = await mutateAsync({
      token,
      mimeType: mimeType.mime,
    });

    statusCodesMapper(response, {
      unhandled: () => {
        dispatch(actions.setLoadingForPhoto(cartItemUUID, photo, false));
        dispatch(actions.setErrorForPhoto(cartItemUUID, photo));
      },
      success: async (data) => {
        const form = new FormData();
        Object.keys(data.fields).forEach((key) =>
          form.append(key, data.fields[key as keyof UploadDataDetails['fields']])
        );
        form.append('file', photo);
        const awsKey = response.data?.fields.key || null;
        await fetch(data.url, { method: 'POST', body: form })
          .then(() => {
            dispatch(actions.setLoadingForPhoto(cartItemUUID, photo, false));
            if (photo.name && awsKey) {
              dispatch(actions.attachPhoto(photo.name, awsKey));
            }
          })
          .catch((err) => {
            captureException(err, Codeowners.CUSTOMER_INTAKE);
            dispatch(actions.setLoadingForPhoto(cartItemUUID, photo, false));
            dispatch(actions.setErrorForPhoto(cartItemUUID, photo));
          });
      },
    });
  };

  return { uploadPhoto };
};
